import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { searchWorkspace } from "@/lib/search.functions";
import {
  ArrowLeftRight,
  BarChart3,
  Bot,
  BrainCircuit,
  CreditCard,
  LayoutDashboard,
  Link2,
  MessageSquare,
  Plus,
  Settings,
  Sparkles,
  Store,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";

// ⌘K command palette: instant navigation, quick actions and global search
// across leads, conversations and listings. Mounted once in DashboardShell.

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/twin", label: "AI Twin", icon: Bot },
  { to: "/knowledge", label: "Knowledge", icon: BrainCircuit },
  { to: "/conversations", label: "Conversations", icon: MessageSquare },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/opportunities", label: "Opportunities", icon: Sparkles },
  { to: "/exchange", label: "Business Exchange", icon: ArrowLeftRight },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/marketplace", label: "Marketplace", icon: Store },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

export function CommandPalette({ companyId }: { companyId?: string | null }) {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const search = useServerFn(searchWorkspace);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults(null);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    debounce.current = setTimeout(() => {
      search({ data: { q: query.trim() } })
        .then(setResults)
        .catch(() => setResults(null));
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, search]);

  function go(to: string) {
    setOpen(false);
    navigate({ to });
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t("app.pages.cmd.searchPh")}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>{t("app.pages.cmd.noResults")}</CommandEmpty>

        {results?.leads?.length > 0 && (
          <CommandGroup heading={t("app.pages.cmd.hLeads")}>
            {results.leads.map((l: any) => (
              <CommandItem key={l.id} value={`lead-${l.id}`} onSelect={() => go("/leads")}>
                <Users className="h-4 w-4" />
                <span className="truncate">
                  {l.name ?? l.email ?? t("app.pages.cmd.leadFallback")}{" "}
                  <span className="text-muted-foreground">· {l.status}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.conversations?.length > 0 && (
          <CommandGroup heading={t("app.pages.cmd.hConversations")}>
            {results.conversations.map((c: any) => (
              <CommandItem key={c.id} value={`conv-${c.id}`} onSelect={() => go("/conversations")}>
                <MessageSquare className="h-4 w-4" />
                <span className="truncate">
                  {c.title ?? c.visitor_name ?? t("app.pages.cmd.convFallback")}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.listings?.length > 0 && (
          <CommandGroup heading={t("app.pages.cmd.hListings")}>
            {results.listings.map((l: any) => (
              <CommandItem key={l.id} value={`listing-${l.id}`} onSelect={() => go("/exchange")}>
                <ArrowLeftRight className="h-4 w-4" />
                <span className="truncate">{l.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results && <CommandSeparator />}

        <CommandGroup heading={t("app.pages.cmd.hActions")}>
          <CommandItem value="action-new-listing" onSelect={() => go("/exchange")}>
            <Plus className="h-4 w-4" /> {t("app.pages.cmd.postDeal")}
          </CommandItem>
          <CommandItem value="action-invite" onSelect={() => go("/settings")}>
            <UserPlus className="h-4 w-4" /> {t("app.pages.cmd.inviteTeammate")}
          </CommandItem>
          {companyId && (
            <CommandItem
              value="action-copy-link"
              onSelect={() => {
                navigator.clipboard
                  .writeText(`https://realitytwin.io/marketplace/${companyId}`)
                  .then(() => toast.success(t("app.pages.cmd.linkCopied")))
                  .catch(() => toast.error(t("app.pages.cmd.couldNotCopy")));
                setOpen(false);
              }}
            >
              <Link2 className="h-4 w-4" /> {t("app.pages.cmd.copyLink")}
            </CommandItem>
          )}
        </CommandGroup>
        <CommandSeparator />

        <CommandGroup heading={t("app.pages.cmd.hGoTo")}>
          {NAV.map((n) => (
            <CommandItem key={n.to} value={`nav-${n.label}`} onSelect={() => go(n.to)}>
              <n.icon className="h-4 w-4" />{" "}
              {t(`app.shell.nav.${n.to.slice(1)}`, { defaultValue: n.label })}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
